import React, { useMemo } from 'react'
import { Menu, Checkbox, Dropdown, Button, Space, Typography, Empty, Flex } from 'antd'
import { SwapOutlined, DeleteOutlined } from '@ant-design/icons'

const { Text } = Typography

/**
 * Left sidebar menu for navigating between patterns within a component
 * Displays patterns with inline actions: select checkbox, move to component, delete
 */
const PatternNavigationMenu = ({
    patterns = [],
    selectedPatternIds = [],
    activePatternId = null,
    onPatternSelect,
    onPatternClick,
    onPatternMove,
    onPatternDelete,
    availableComponents = [],
    currentComponentId,
    form,
}) => {
    // Create dropdown menu items for moving pattern to another component
    const getMoveMenuItems = (patternId) => {
        const otherComponents = availableComponents.filter(c => c.id !== currentComponentId)

        if (otherComponents.length === 0) return []

        return otherComponents.map(component => ({
            key: component.id,
            label: component.name,
            onClick: () => onPatternMove(patternId, component.id),
        }))
    }

    // Handle checkbox click without triggering menu item selection
    const handleCheckboxClick = (e, patternId) => {
        e.stopPropagation()
        e.domEvent?.stopPropagation()
        onPatternSelect(patternId, e.target.checked)
    }

    // Handle delete button click
    const handleDeleteClick = (e, patternId) => {
        e.stopPropagation()
        e.domEvent?.stopPropagation()
        onPatternDelete(patternId)
    }

    // Create menu items from patterns
    const menuItems = useMemo(() => {
        return patterns.map(pattern => {
            const isSelected = selectedPatternIds.includes(pattern.id)
            const moveMenuItems = getMoveMenuItems(pattern.id)

            return {
                key: pattern.id,
                label: (
                    <Flex justify='space-between' align='center' width='100%' gap={8}>
                            <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleCheckboxClick(e, pattern.id)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <Text
                                ellipsis={{ rows: 3, tooltip: pattern.name }}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {pattern.name}
                            </Text>
                        <Space size={0} onClick={(e) => e.stopPropagation()}>
                            {moveMenuItems.length > 0 && (
                                <Dropdown
                                    menu={{ items: moveMenuItems }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        title="Move To"
                                        icon={<SwapOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </Dropdown>
                            )}
                            <Button
                                type="text"
                                size="small"
                                danger
                                title="Delete"
                                icon={<DeleteOutlined />}
                                onClick={(e) => handleDeleteClick(e, pattern.id)}
                            />
                        </Space>
                    </Flex>
                ),
                onClick: () => onPatternClick(pattern.id),
            }
        })
    }, [
        patterns,
        selectedPatternIds,
        form,
        availableComponents,
        currentComponentId,
        onPatternSelect,
        onPatternClick,
        onPatternMove,
        onPatternDelete,
    ])

    if (patterns.length === 0) {
        return <Empty description='No patterns in this component' image={Empty.PRESENTED_IMAGE_SIMPLE}/>
    }

    return (
        <Menu
            items={menuItems}
            selectedKeys={activePatternId ? [String(activePatternId)] : []}
            style={{
                height: '100%',
                border: 'none',
            }}
            className="pattern-navigation-menu"
        />
    )
}

export default React.memo(PatternNavigationMenu)
